import { useEffect, useMemo, useState } from "react";
import Navbar from "../../components/navbar/navbar";
import { useNavigate } from "react-router-dom";
import "../config/config.scss";
import api from "../../services/api";
import Swal from "sweetalert2";

import {
  ShieldCheck,
  UserPlus,
  Pencil,
  Trash2,
  Eye,
  X,
  Clock3,
  Save,
  LogOut
} from "lucide-react";

/** Ajusta según tus roles reales */
const ROLES = [
  { id: 1, nombre: "Administrador" },
  { id: 2, nombre: "Contribuyente" },
];

const ADMIN_ROLE_ID = 1;
const CONTRIBUYENTE_ROLE_ID = 2;

/** Solo roles permitidos al crear */
const CREATE_ROLES = ROLES.filter((r) => r.id === CONTRIBUYENTE_ROLE_ID);

const ROLES_BY_ID = ROLES.reduce((acc, r) => {
  acc[r.id] = r.nombre;
  return acc;
}, {});

const isProtectedAdminUser = (user) => {
  const rolId = Number(user?.rol_id ?? 0);
  const rolNombre = String(user?.rol ?? "").toLowerCase();
  return rolId === ADMIN_ROLE_ID || rolNombre.includes("admin");
};

const badgeClassByRol = (rol) => {
  const r = (rol || "").toLowerCase();

  if (r.includes("admin") || r.includes("administr")) return "badge badge--admin";
  if (r.includes("contrib")) return "badge badge--contribuyente";

  return "badge";
};

const maskPassword = () => "••••••••";

const formatFecha = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString("es-MX");
};

const normalizeUser = (u) => {
  const nombre = u?.nombre ?? u?.usuario ?? u?.username ?? "";
  const email = u?.email ?? "";

  const rol_id = Number(u?.rol_id ?? u?.rolId ?? u?.rol?.id ?? 0) || 0;
  const rol =
    u?.rol?.nombre ??
    u?.rol ??
    ROLES_BY_ID[rol_id] ??
    "";

  const fechaAlta =
    u?.fechaAlta ??
    u?.fecha_alta ??
    u?.created_at ??
    "";

  return {
    id: u?.id,
    usuario: nombre,
    email,
    rol_id,
    rol,
    fechaAlta: formatFecha(fechaAlta)
  };
};

const getFirst422Error = (err) => {
  const data = err?.response?.data;
  const errors = data?.errors || {};
  return {
    emailErr: errors?.email?.[0],
    passErr: errors?.password?.[0],
    rolErr: errors?.rol_id?.[0],
    anyErr:
      errors?.email?.[0] ||
      errors?.password?.[0] ||
      errors?.rol_id?.[0] ||
      data?.message ||
      "Validación fallida."
  };
};

const Config = () => {
  const navigate = useNavigate();

  // ===== Auth / permisos =====
  const token = localStorage.getItem("token") || sessionStorage.getItem("token");
  const tokenType =
    localStorage.getItem("token_type") ||
    sessionStorage.getItem("token_type") ||
    "Bearer";

  const currentRolId = Number(
    localStorage.getItem("rol_id") || sessionStorage.getItem("rol_id") || 0
  );

  const isAdmin = currentRolId === ADMIN_ROLE_ID;

  

  // ===== Helpers Swal =====
  const fireSwal = async (opts, onClose) => {
    if (typeof onClose === "function") onClose();
    return Swal.fire(opts);
  };

  const show422Swal = async (err, onClose) => {
    const { emailErr, passErr, anyErr } = getFirst422Error(err);

    if (
      emailErr &&
      /válid|valid email|email address/i.test(emailErr) &&
      !/registrad|taken|exists|ya está/i.test(emailErr)
    ) {
      return fireSwal(
        {
          icon: "error",
          title: "Correo inválido",
          text: emailErr,
          confirmButtonText: "Ok"
        },
        onClose
      );
    }

    if (emailErr && /registrad|taken|exists|ya está/i.test(emailErr)) {
      return fireSwal(
        {
          icon: "warning",
          title: "Correo ya registrado",
          text: emailErr,
          confirmButtonText: "Ok"
        },
        onClose
      );
    }

    if (passErr && /6|min/i.test(passErr)) {
      return fireSwal(
        {
          icon: "error",
          title: "Contraseña inválida",
          text: passErr,
          confirmButtonText: "Ok"
        },
        onClose
      );
    }

    return fireSwal(
      {
        icon: "error",
        title: "No se pudo guardar",
        text: anyErr,
        confirmButtonText: "Ok"
      },
      onClose
    );
  };

  // ===== Data =====
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  // ===== Edit =====
  const [editingId, setEditingId] = useState(null);
  const [showPassId, setShowPassId] = useState(null);

  const editingRow = useMemo(
    () => rows.find((r) => r.id === editingId) || null,
    [rows, editingId]
  );

  const editingIsProtectedAdmin = useMemo(  
    () => isProtectedAdminUser(editingRow),
    [editingRow]
  );

  const [form, setForm] = useState({
    usuario: "",
    password: "",
    rol_id: CONTRIBUYENTE_ROLE_ID
  });

  // ===== Modal crear =====
  const [openCreate, setOpenCreate] = useState(false);
  const [createForm, setCreateForm] = useState({
    nombre: "",
    email: "",
    password: "",
    rol_id: CONTRIBUYENTE_ROLE_ID
  });

  const closeCreateModal = () => setOpenCreate(false);

  const clearAuthStorage = () => {
    const keys = [
      "token",
      "token_type",
      "rol_id",
      "empresa_id",
      "user",
      "nombre",
      "email"
    ];

    keys.forEach((k) => {
      localStorage.removeItem(k);
      sessionStorage.removeItem(k);
    });
  };

  const handleLogout = async () => {
    const confirm = await Swal.fire({
      icon: "question",
      title: "¿Cerrar sesión?",
      text: "Tu sesión se cerrará en este dispositivo.",
      showCancelButton: true,
      confirmButtonText: "Sí, salir",
      cancelButtonText: "Cancelar"
    });

    if (!confirm.isConfirmed) return;

    try {
      if (token) {
        await api.post("/logout");
      }
    } catch (err) {
      console.error("Error en /logout:", err);
    } finally {
      clearAuthStorage();
      navigate("/login", { replace: true });
    }
  };

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const res = await api.get("/usuarios");
      const payload = res.data;
      const list =
        (Array.isArray(payload) && payload) ||
        payload?.items ||
        payload?.data ||
        [];

      const normalized = Array.isArray(list) ? list.map(normalizeUser) : [];
      setRows(normalized);
    } catch (err) {
      console.error("Error cargando usuarios:", err);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const tokenSaved = localStorage.getItem("token") || sessionStorage.getItem("token");
    if (!tokenSaved) {
      navigate("/login", { replace: true });
    }
  }, [navigate]);

  // ===== Edit flow =====
  const startEdit = (row) => {
    if (!isAdmin) return;

    setEditingId(row.id);
    setShowPassId(null);
    setForm({
      usuario: row.usuario,
      password: "",
      rol_id: Number(row.rol_id) || CONTRIBUYENTE_ROLE_ID
    });
  };

  const closeEdit = () => {
    setEditingId(null);
    setShowPassId(null);
    setForm({
      usuario: "",
      password: "",
      rol_id: CONTRIBUYENTE_ROLE_ID
    });
  };

  const updateUser = async () => {
    if (!isAdmin || !editingId || !editingRow) return;

    const pass = (form.password || "").trim();
    const rolIdNum = Number(form.rol_id) || CONTRIBUYENTE_ROLE_ID;
    const protectedAdmin = isProtectedAdminUser(editingRow);

    if (protectedAdmin && rolIdNum !== ADMIN_ROLE_ID) {
      await Swal.fire({
        icon: "warning",
        title: "Acción no permitida",
        text: "No se puede cambiar el rol de un usuario Administrador.",
        confirmButtonText: "Ok"
      });
      return;
    }

    if (pass.length > 0 && pass.length < 6) {
      await fireSwal(
        {
          icon: "error",
          title: "Contraseña inválida",
          text: "La contraseña debe tener al menos 6 caracteres.",
          confirmButtonText: "Ok"
        },
        closeEdit
      );
      return;
    }

    try {
      const body = {
        rol_id: protectedAdmin ? ADMIN_ROLE_ID : rolIdNum
      };

      if (pass.length > 0) {
        body.password = pass;
      }

      await api.put(`/usuarios/${editingId}`, body);
      await fetchUsers();

      await Swal.fire({
        icon: "success",
        title: "Actualizado",
        text:
          pass.length > 0
            ? "Contraseña actualizada correctamente."
            : "Usuario actualizado correctamente.",
        timer: 1400,
        showConfirmButton: false
      });

      closeEdit();
    } catch (err) {
      const status = err?.response?.status;

      if (status === 422) {
        await show422Swal(err, closeEdit);
        return;
      }

      if (status === 403) {
        await Swal.fire({
          icon: "error",
          title: "No permitido",
          text:
            err?.response?.data?.message ||
            "No se permite modificar el rol de un usuario Administrador.",
          confirmButtonText: "Ok"
        });
        return;
      }

      await fireSwal(
        {
          icon: "error",
          title: "Error",
          text: "Ocurrió un error al actualizar el usuario.",
          confirmButtonText: "Ok"
        },
        closeEdit
      );

      console.error("Error actualizando usuario:", err);
    }
  };

  // ===== Delete =====
  const deleteUser = async (id) => {
    if (!isAdmin) return;

    const userToDelete = rows.find((u) => u.id === id);

    if (isProtectedAdminUser(userToDelete)) {
      await Swal.fire({
        icon: "warning",
        title: "Acción no permitida",
        text: "No se puede eliminar un usuario con rol Administrador.",
        confirmButtonText: "Ok"
      });
      return;
    }

    const res = await Swal.fire({
      icon: "warning",
      title: "¿Eliminar usuario?",
      text: "Esta acción no se puede deshacer.",
      showCancelButton: true,
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar"
    });

    if (!res.isConfirmed) return;

    try {
      await api.delete(`/usuarios/${id}`);
      await fetchUsers();

      if (editingId === id) closeEdit();

      await Swal.fire({
        icon: "success",
        title: "Eliminado",
        text: "Usuario eliminado correctamente.",
        timer: 1300,
        showConfirmButton: false
      });
    } catch (err) {
      await Swal.fire({
        icon: "error",
        title: "Error",
        text: err?.response?.data?.message || "No se pudo eliminar el usuario.",
        confirmButtonText: "Ok"
      });
      console.error("Error eliminando usuario:", err);
    }
  };

  // ===== Create =====
  const openCreateModal = () => {
    if (!isAdmin) return;
    setCreateForm({
      nombre: "",
      email: "",
      password: "",
      rol_id: CONTRIBUYENTE_ROLE_ID
    });
    setOpenCreate(true);
  };

  const createUser = async () => {
    if (!isAdmin) return;

    const nombre = (createForm.nombre || "").trim();
    const email = (createForm.email || "").trim();
    const password = (createForm.password || "").trim();

    // Siempre contribuyente
    const rolIdNum = CONTRIBUYENTE_ROLE_ID;

    if (!nombre || !email || !password) {
      await fireSwal(
        {
          icon: "warning",
          title: "Campos incompletos",
          text: "Completa nombre, email y contraseña.",
          confirmButtonText: "Ok"
        },
        closeCreateModal
      );
      return;
    }

    if (password.length < 6) {
      await fireSwal(
        {
          icon: "error",
          title: "Contraseña inválida",
          text: "La contraseña debe tener al menos 6 caracteres.",
          confirmButtonText: "Ok"
        },
        closeCreateModal
      );
      return;
    }

    try {
      await api.post("/usuarios", {
        nombre,
        email,
        password,
        rol_id: rolIdNum
      });

      await fetchUsers();
      closeCreateModal();

      await Swal.fire({
        icon: "success",
        title: "Creado",
        text: "Usuario creado correctamente.",
        timer: 1400,
        showConfirmButton: false
      });
    } catch (err) {
      if (err?.response?.status === 422) {
        await show422Swal(err, closeCreateModal);
        return;
      }

      await fireSwal(
        {
          icon: "error",
          title: "Error",
          text: "Ocurrió un error al crear el usuario.",
          confirmButtonText: "Ok"
        },
        closeCreateModal
      );

      console.error("Error creando usuario:", err);
    }
  };

  const onCreateKeyDown = (e) => {
    if (e.key === "Escape") closeCreateModal();
    if (e.key === "Enter") createUser();
  };

  return (
    <div className="body-user">
      <Navbar />

      <div className="user-page">
        <div className="user-header">
          <div className="user-title">
            <div className="title-icon">
              <ShieldCheck size={22} />
            </div>
            <div>
              <h1>Gestor de Usuarios</h1>
              <p>Administración de accesos y roles del sistema.</p>
            </div>
          </div>

          <div className="buttons-header">
            {isAdmin ? (
              <button className="btn-primary" type="button" onClick={openCreateModal}>
                <UserPlus size={18} />
                Crear Usuario
              </button>
            ) : null}

            <button className="btn-close" type="button" onClick={handleLogout}>
              <LogOut size={18} />
              Cerrar Sesión
            </button>
          </div>
        </div>

        {loading ? (
          <div className="card" style={{ padding: 12 }}>
            Cargando usuarios...
          </div>
        ) : null}

        {/* Modal Crear */}
        {openCreate && isAdmin && (
          <div className="modal-backdrop" onMouseDown={closeCreateModal}>
            <div
              className="modal-card"
              onMouseDown={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
              onKeyDown={onCreateKeyDown}
              tabIndex={-1}
            >
              <div className="modal-head">
                <div className="modal-title">
                  <UserPlus size={18} />
                  <span><b>Crear Usuario</b></span>
                </div>

                <button
                  className="icon-btn"
                  type="button"
                  onClick={closeCreateModal}
                  aria-label="Cerrar"
                >
                  <X size={18} />
                </button>
              </div>

              <div className="modal-body">
                <div className="field">
                  <label>NOMBRE</label>
                  <div className="input">
                    <span className="input-prefix">👤</span>
                    <input
                      value={createForm.nombre}
                      onChange={(e) =>
                        setCreateForm((p) => ({ ...p, nombre: e.target.value }))
                      }
                      placeholder="Nombre"
                      autoFocus
                    />
                  </div>
                </div>

                <div className="field">
                  <label>EMAIL</label>
                  <div className="input">
                    <span className="input-prefix">@</span>
                    <input
                      type="email"
                      value={createForm.email}
                      onChange={(e) =>
                        setCreateForm((p) => ({ ...p, email: e.target.value }))
                      }
                      placeholder="correo@dominio.com"
                    />
                  </div>
                </div>

                <div className="field">
                  <label>CONTRASEÑA</label>
                  <div className="input">
                    <span className="input-prefix">🔒</span>
                    <input
                      type="password"
                      value={createForm.password}
                      onChange={(e) =>
                        setCreateForm((p) => ({ ...p, password: e.target.value }))
                      }
                      placeholder="mín. 6 caracteres"
                    />
                  </div>
                </div>

                <div className="field">
                  <label>ROL</label>
                  <div className="input">
                    <select
                      value={createForm.rol_id}
                      onChange={(e) =>
                        setCreateForm((p) => ({
                          ...p,
                          rol_id: Number(e.target.value)
                        }))
                      }
                    >
                      {CREATE_ROLES.map((r) => (
                        <option key={r.id} value={r.id}>
                          {r.nombre}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="modal-actions">
                  <button className="btn-warning" type="button" onClick={createUser}>
                    <Save size={18} />
                    Guardar
                  </button>

                  <button
                    className="icon-btn"
                    type="button"
                    onClick={closeCreateModal}
                    title="Cancelar"
                  >
                    <X size={18} />
                  </button>
                </div>

                <div className="modal-hint">
                  Tip: Enter = Guardar, Esc = Cerrar
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Panel Edit */}
        {editingRow && isAdmin && (
          <div className="card edit-card">
            <div className="edit-top">
              <div className="edit-label">
                <Pencil size={18} />
                <span>
                  <b>Editando Usuario:</b> {editingRow.usuario}
                </span>
              </div>

              <button
                className="icon-btn"
                type="button"
                onClick={closeEdit}
                aria-label="Cerrar edición"
              >
                <X size={18} />
              </button>
            </div>

            <div className="edit-grid">
              <div className="field">
                <label>USUARIO</label>
                <div className="input">
                  <span className="input-prefix">@</span>
                  <input
                    value={form.usuario}
                    readOnly
                    disabled
                    title="No se puede modificar"
                  />
                </div>
              </div>

              <div className="field">
                <label>CONTRASEÑA (opcional)</label>
                <div className="input">
                  <span className="input-prefix">🔒</span>
                  <input
                    type={showPassId === editingId ? "text" : "password"}
                    value={form.password}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, password: e.target.value }))
                    }
                    placeholder="dejar vacío para conservar"
                  />
                  <button
                    className="input-suffix-btn"
                    type="button"
                    onClick={() =>
                      setShowPassId((prev) => (prev === editingId ? null : editingId))
                    }
                    title="Ver lo que estás escribiendo"
                  >
                    <Eye size={18} />
                  </button>
                </div>
              </div>

              <div className="field">
                <label>ROL</label>
                <div className="input">
                  <select
                    value={form.rol_id}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, rol_id: Number(e.target.value) }))
                    }
                    disabled={editingIsProtectedAdmin}
                    title={
                      editingIsProtectedAdmin
                        ? "El rol del Administrador no se puede modificar"
                        : ""
                    }
                  >
                    {ROLES.map((r) => (
                      <option key={r.id} value={r.id}>
                        {r.nombre}
                      </option>
                    ))}
                  </select>
                </div>

                {editingIsProtectedAdmin ? (
                  <small style={{ color: "#b45309", fontWeight: 600 }}>
                    El rol del usuario Administrador no se puede modificar.
                  </small>
                ) : null}
              </div>

              <div className="edit-actions">
                <button className="btn-warning" type="button" onClick={updateUser}>
                  <Save size={18} />
                  Actualizar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Tabla */}
        <div className="card table-card">
          <div className="table">
            <div className="thead">
              <div>USUARIO</div>
              <div>ROL ASIGNADO</div>
              <div>CONTRASEÑA</div>
              <div>FECHA ALTA</div>
              <div className="right">ACCIONES</div>
            </div>

            <div className="tbody">
              {rows.map((r) => {
                const rowIsProtectedAdmin = isProtectedAdminUser(r);

                return (
                  <div className="trow" key={r.id}>
                    <div className="user-col">
                      <div className="avatar">👤</div>
                      <div className="username">{r.usuario}</div>
                    </div>

                    <div>
                      <span className={badgeClassByRol(r.rol)}>
                        {(r.rol || "").toUpperCase()}
                      </span>
                    </div>

                    <div className="pass-col">
                      <span className="pass">{maskPassword()}</span>
                    </div>

                    <div className="date-col">
                      <Clock3 size={16} />
                      <span>{r.fechaAlta || "—"}</span>
                    </div>

                    <div className="actions right">
                      {isAdmin ? (
                        <>
                          <button
                            className="icon-btn"
                            type="button"
                            onClick={() => startEdit(r)}
                            title="Editar"
                          >
                            <Pencil size={18} />
                          </button>

                          {!rowIsProtectedAdmin ? (
                            <button
                              className="icon-btn danger"
                              type="button"
                              onClick={() => deleteUser(r.id)}
                              title="Eliminar"
                            >
                              <Trash2 size={18} />
                            </button>
                          ) : (
                            <button
                              className="icon-btn danger"
                              type="button"
                              disabled
                              title="No se puede eliminar un Administrador"
                              style={{ opacity: 0.45, cursor: "not-allowed" }}
                            >
                              <Trash2 size={18} />
                            </button>
                          )}
                        </>
                      ) : (
                        <span style={{ opacity: 0.6 }}>—</span>
                      )}
                    </div>
                  </div>
                );
              })}

              {!loading && rows.length === 0 ? (
                <div className="trow" style={{ opacity: 0.7 }}>
                  <div>No hay usuarios para mostrar.</div>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Config;